import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowLeft, Clock, Image as ImageIcon, Ticket, UserPlus, Settings, Lock } from 'lucide-react';
import { eventsService, rsvpService } from '../services/api-service';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import './EventDetails.css';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    name: user?.name || '',
    phone: '',
    startingLocation: ''
  });
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeValidated, setCodeValidated] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [id]);

  // Check if user has access to secret event
  useEffect(() => {
    if (event && event.isPromotional && event.locationHidden && user) {
      // Check if user has this code (would need to check user's promotional codes)
      // For now, we'll show the modal and let them enter the code
      setShowCodeModal(true);
    } else if (event && event.isPromotional && event.locationHidden && !user) {
      // Not logged in, show code modal too (they'll need to login after)
      setShowCodeModal(true);
    }
  }, [event, user]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventData = await eventsService.getEventById(id);
      if (eventData) {
        setEvent(eventData);
      } else {
        setError('Event not found');
      }
    } catch (error) {
      console.error('Error loading event:', error);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setCodeError('');
    setCodeLoading(true);

    if (!user) {
      setCodeError('Please login first to validate the code');
      setCodeLoading(false);
      navigate('/?login=true');
      return;
    }

    try {
      const result = await api.events.validateCode(event.id, secretCode);
      if (result.success && result.valid && result.accessGranted) {
        setCodeValidated(true);
        setShowCodeModal(false);
        setSecretCode('');
        // Reload event to get full details
        loadEvent();
      } else {
        setCodeError(result.message || 'Invalid promotional code');
      }
    } catch (error) {
      console.error('Error validating code:', error);
      setCodeError(error.response?.data?.error || 'Failed to validate code. Please try again.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleRSVP = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/?login=true');
      return;
    }

    setRsvpLoading(true);
    try {
      await rsvpService.createRSVP({
        eventId: event.id,
        name: rsvpData.name,
        phone: rsvpData.phone,
        startingLocation: rsvpData.startingLocation,
        userId: user.id
      });
      setRsvpSubmitted(true);
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowRSVPModal(false);
        setRsvpSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Error creating RSVP:', error);
      alert('Failed to RSVP. Please try again.');
    } finally {
      setRsvpLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <div className="container">
          <div className="event-details-loading">
            <Calendar size={64} className="spinning" />
            <h3>Loading event details...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-details-page">
        <div className="container">
          <div className="event-details-error">
            <h2>Event Not Found</h2>
            <p>{error || 'The event you are looking for does not exist.'}</p>
            <Link to="/events" className="btn">
              <ArrowLeft size={20} />
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="event-details-page">
      {/* Header with Back Button */}
      <section className="event-details-header">
        <div className="container">
          <Link to="/events" className="event-details-back">
            <ArrowLeft size={20} />
            Back to Events
          </Link>
        </div>
      </section>

      {/* Event Hero */}
      <section className="event-details-hero">
        <div className="container">
          {event.coverImage && (
            <div className="event-details-image">
              <img src={event.coverImage} alt={event.title} />
            </div>
          )}
          <div className="event-details-hero-content">
            <h1>{event.title}</h1>
            <div className="event-details-meta">
              <div className="event-details-meta-item">
                <Calendar size={20} />
                <span>{formatDate(event.date)}</span>
              </div>
              {event.location && (
                <div className="event-details-meta-item">
                  <MapPin size={20} />
                  <span>{typeof event.location === 'string' ? event.location : (event.location.venue || event.location.address)}</span>
                </div>
              )}
              {event.time && (
                <div className="event-details-meta-item">
                  <Clock size={20} />
                  <span>{event.time.start} - {event.time.end}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Event Content */}
      <section className="event-details-content section">
        <div className="container">
          <div className="event-details-grid">
            {/* Main Content */}
            <div className="event-details-main">
              <div className="event-details-description">
                <h2>About This Event</h2>
                <p>{event.description || 'Join us for an amazing celebration!'}</p>
              </div>

              {/* Gallery */}
              {event.gallery && event.gallery.length > 0 && (
                <div className="event-details-gallery">
                  <h2>
                    <ImageIcon size={24} />
                    Event Gallery
                  </h2>
                  <div className="event-details-gallery-grid">
                    {event.gallery.map((photo, index) => (
                      <div key={photo.id || index} className="event-details-gallery-item">
                        <img 
                          src={photo.url || photo.thumbnailUrl || photo} 
                          alt={`${event.title} - Photo ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Artists */}
              {event.artists && event.artists.length > 0 && (
                <div className="event-details-artists">
                  <h2>Artists & Performers</h2>
                  <div className="event-details-artists-list">
                    {event.artists.map((artist, index) => (
                      <div key={artist.id || index} className="event-details-artist">
                        {artist.image && (
                          <img src={artist.image} alt={artist.name} />
                        )}
                        <div>
                          <h3>{artist.name}</h3>
                          {artist.bio && <p>{artist.bio}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="event-details-sidebar">
              {/* RSVP/Ticket Section */}
              <div className="event-details-actions">
                <h3>Join This Event</h3>
                {user ? (
                  <>
                    <button 
                      className="btn btn-orange btn-block"
                      onClick={() => setShowRSVPModal(true)}
                    >
                      <UserPlus size={20} />
                      RSVP Now
                    </button>
                    <button 
                      className="btn btn-block"
                      onClick={() => navigate(`/calendar?event=${event.id}`)}
                    >
                      <Ticket size={20} />
                      Purchase Ticket
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn btn-orange btn-block"
                    onClick={() => navigate('/?login=true')}
                  >
                    <UserPlus size={20} />
                    Login to RSVP
                  </button>
                )}
              </div>

              {/* Admin Actions */}
              {user && (
                <div className="event-details-admin">
                  <h3>Admin</h3>
                  <a
                    href={`/events/${event.id}/location-reveal`}
                    className="btn btn-block"
                  >
                    <Settings size={18} />
                    Location Reveal Admin
                  </a>
                </div>
              )}

              {/* Event Info */}
              <div className="event-details-info">
                <h3>Event Information</h3>
                <div className="event-details-info-item">
                  <strong>Status:</strong>
                  <span className={`event-status-badge ${event.status}`}>
                    {event.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                  </span>
                </div>
                {event.attendees && (
                  <div className="event-details-info-item">
                    <Users size={18} />
                    <span>{event.attendees} attendees</span>
                  </div>
                )}
                {event.tags && event.tags.length > 0 && (
                  <div className="event-details-info-item">
                    <strong>Tags:</strong>
                    <div className="event-details-tags">
                      {event.tags.map((tag, index) => (
                        <span key={index} className="event-details-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secret Code Entry Modal */}
      {showCodeModal && event && event.isPromotional && event.locationHidden && !codeValidated && (
        <div className="rsvp-modal-overlay" onClick={() => {}}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🔒 Secret Event</h2>
            <p className="rsvp-modal-subtitle">
              This event requires a promotional code to view details and location.
            </p>
            {!user && (
              <div className="code-modal-login-prompt">
                <p>Please login first to validate your code.</p>
                <button 
                  className="btn btn-orange btn-block"
                  onClick={() => navigate('/?login=true')}
                >
                  Login
                </button>
              </div>
            )}
            {user && (
              <form onSubmit={handleCodeSubmit} className="rsvp-form">
                <div className="rsvp-form-group">
                  <label htmlFor="secretCode">Enter Promotional Code</label>
                  <input
                    type="text"
                    id="secretCode"
                    value={secretCode}
                    onChange={(e) => {
                      setSecretCode(e.target.value);
                      setCodeError('');
                    }}
                    placeholder="Enter secret code"
                    required
                    autoFocus
                  />
                  {codeError && (
                    <div className="rsvp-form-error">{codeError}</div>
                  )}
                </div>
                <div className="rsvp-form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-orange btn-block"
                    disabled={codeLoading}
                  >
                    {codeLoading ? 'Validating...' : 'Validate Code'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-block"
                    onClick={() => {
                      setShowCodeModal(false);
                      navigate('/events');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      {showRSVPModal && (
        <div className="rsvp-modal-overlay" onClick={() => setShowRSVPModal(false)}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            <h2>RSVP for {event.title}</h2>
            {rsvpSubmitted ? (
              <div className="rsvp-success">
                <UserPlus size={48} />
                <h3>RSVP Confirmed!</h3>
                <p>You're all set. Location details will be sent to you closer to the event.</p>
              </div>
            ) : (
              <form onSubmit={handleRSVP}>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-name">Name *</label>
                  <input
                    type="text"
                    id="rsvp-name"
                    value={rsvpData.name}
                    onChange={(e) => setRsvpData({ ...rsvpData, name: e.target.value })}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-phone">Phone *</label>
                  <input
                    type="tel"
                    id="rsvp-phone"
                    value={rsvpData.phone}
                    onChange={(e) => setRsvpData({ ...rsvpData, phone: e.target.value })}
                    required
                    placeholder="Your phone number"
                  />
                </div>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-location">Starting Location *</label>
                  <input
                    type="text"
                    id="rsvp-location"
                    value={rsvpData.startingLocation}
                    onChange={(e) => setRsvpData({ ...rsvpData, startingLocation: e.target.value })}
                    required
                    placeholder="Where will you be traveling from?"
                  />
                  <small>This helps us send location details at the right time</small>
                </div>
                <div className="rsvp-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRSVPModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-orange" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Submitting...' : 'Confirm RSVP'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;

